// vg-binding: CONTEXT:decisions (P999.11.D-09/D-14/D-15/D-16/D-04)
// vg-binding: INTERFACE-STANDARDS:error-shape
// apps/admin/src/pages/products/ProductApprovalPage.tsx
//
// Phase 8.2 Wave 6 Task 41 — Admin Product Approval queue.
//
// vg-binding: PLAN:task-41
// vg-binding: CONTEXT:decisions=P8.D-188,P8.D-216,P8.D-307,P8.D-309,P8.D-310,P8.D-311
// vg-binding: TEST-GOALS:G-33
//
// Per CONTEXT.md (new admin page `/products/product-approval`) +
//  (5-column queue: vendor / product / category / submitted date
// type) + (single queue for new submissions + re-approvals
// re-approval rows have diff icon). Backend (Task 30
// product-approval.handler.ts) exposes:
//   GET    /api/v1/admin/product-approvals?page=&limit=&type=&status=&sort=
//   POST   /api/v1/admin/product-approvals/upload
//   POST   /api/v1/admin/product-approvals/:id/approve
//   POST   /api/v1/admin/product-approvals/:id/reject
//
// Goals covered: (vendor product approval surface).
// Test IDs:
//   product-approval-upload-btn
//   product-approval-approve-btn-{approvalId}
//   product-approval-reject-btn-{approvalId}
//   product-approval-diff-btn-{approvalId}
//   product-approval-diff-modal       (rendered by ApprovalDiffModal)
//   product-approval-upload-dialog    (rendered by ApprovalUploadDialog)

import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useSearchParams } from 'react-router-dom';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  useToast,
  PageHeader,
} from '@printway/ui';

import {
  ApprovalDiffModal,
  type ApprovalDiffEntry,
  type ApprovalMapping,
} from '../../features/products/ApprovalDiffModal.js';
import { ApprovalUploadDialog } from '../../features/products/ApprovalUploadDialog.js';
// dbg (2026-07-06) UAT #437 — the upload dialog needs a vendor list to render
// its picker; without it vendorId stays '' and "Upload/preview" is disabled
// forever. Reuse the admin vendor-options endpoint used by the push-vendor picker.
import { listVendorOptions } from '../../api/support-tickets.api.js';
import { useAuthStore } from '../../stores/useAuthStore.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Wire types — mirror product-approval.handler.ts list response shape
// ---------------------------------------------------------------------------

// Phase 8.2.1 — admin self-create joins the unified queue with
// actorType discrimination (vendor | admin).
type ApprovalType = 'new_submission' | 're_approval' | 'admin_self_create';
type ApprovalActorType = 'vendor' | 'admin';
type ApprovalStatus = 'pending' | 'approved' | 'rejected';
// Phase 8.2.2 — submission source filter chip.
type ApprovalSource = 'vendor_self_submit' | 'admin_excel_upload';

interface ApprovalRow {
  id: string;
  vendor_id: string | null;
  vendor_name: string;
  product_name: string;
  category_id: string | null;
  category_name: string | null;
  submitted_at: string;
  type: ApprovalType;
  status: ApprovalStatus;
  /**
   * Phase 8.2.1 — actor discriminator. 'vendor' = legacy vendor flow
   * 'admin' = admin self-create (PRODUCT_APPROVAL_ACTOR_TYPES). Optional for
   * backward compat with rows lacking the field; treated as 'vendor' default.
   */
  actor_type?: ApprovalActorType;
  actor_user_id?: string | null;
  actor_email?: string | null;
  /** Phase 8.2.2 — submission origin. Empty rows fallback to admin_excel_upload via BE default. */
  source?: ApprovalSource;
  /** Phase 8.2.2 — non-null for vendor self-submit rows, links back to Phase 7 VendorProduct. */
  vendor_product_id?: string | null;
  /** Phase 8.2.2 — admin Excel multi-vendor apply targets resolved on approve. */
  apply_to_vendor_ids?: string[];
  has_diff: boolean;
  /** Vendor product mapped to a Printway blueprint (2026-10-13). */
  mapped?: boolean;
  original_blueprint_id: string | null;
  /**
   * dbg-approval-name-link — blueprint created by admin self-create (pending
   * or approved). Used to link the product name → blueprint detail when
   * original_blueprint_id is null (admin self-create rows).
   */
  approved_blueprint_id?: string | null;
  /**  duplicate detection — server may include this on payload-fetch. */
  duplicate_warnings?: Array<{
    blueprintId: string;
    name: string;
    similarity: number;
  }>;
}

interface ApprovalListMeta {
  page: number;
  limit: number;
  total: number;
  pending_count: number;
}

interface ApprovalListResponse {
  data: ApprovalRow[];
  meta: ApprovalListMeta;
}

interface ApiErrorEnvelope {
  ok?: false;
  error?: { code?: string; message?: string; user_message?: string; skus?: unknown };
}

type LooseClient = {
  GET: (
    path: string,
    init?: { headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
  POST: (
    path: string,
    init: { body?: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// dbg-approve-i18n — map BE error CODEs (English wire values) to localized
// labels so the toast honours the active locale instead of echoing the raw
// server message. Codes mirror ERROR_CODE_TO_STATUS in the BE admin.handler.
const APPROVAL_ERROR_CODE_TO_KEY: Record<string, string> = {
  FOUR_EYES_REQUIRED: 'product_approval.errors.fourEyesRequired',
  OVERRIDE_REASON_REQUIRED: 'product_approval.errors.overrideReasonRequired',
  BLUEPRINT_NOT_READY_FOR_PUBLISH: 'product_approval.errors.notReadyForPublish',
  BLUEPRINT_NOT_FOUND: 'product_approval.errors.blueprintNotFound',
  APPROVAL_NOT_FOUND: 'product_approval.errors.approvalNotFound',
  ALREADY_RESOLVED: 'product_approval.errors.alreadyResolved',
  CONFLICT_RETRY: 'product_approval.errors.conflictRetry',
  CATEGORY_NOT_FOUND: 'product_approval.errors.categoryNotFound',
  // 2026-10-13 — linking a vendor product mapped to Printway SKUs.
  COST_GUARD_VIOLATION: 'product_approval.errors.costGuard',
  ALREADY_LISTED: 'product_approval.errors.alreadyListed',
  BLUEPRINT_UNAVAILABLE: 'product_approval.errors.blueprintUnavailable',
  PRICING_MISSING: 'product_approval.errors.pricingMissing',
  VARIANT_SPEC_OUT_OF_RANGE: 'product_approval.errors.variantSpecOutOfRange',
};

/**
 * Resolve an API error payload to a user-facing string. When the error CODE
 * has a localized label, use it (locale-honouring). Otherwise fall back to
 * the server message, then the generic fallback. `translate` is the bound
 * i18n `t`; `hasKey` guards against missing keys (returns key === value).
 */
function extractApiMessage(
  payload: unknown,
  fallback: string,
  translate?: (key: string) => string,
): string {
  if (payload && typeof payload === 'object') {
    const env = payload as ApiErrorEnvelope;
    const code = env.error?.code;
    if (code && translate) {
      const key = APPROVAL_ERROR_CODE_TO_KEY[code];
      if (key) {
        const label = translate(key);
        // i18next returns the key itself when the key is missing.
        if (label && label !== key) return label;
      }
    }
    if (env.error?.user_message) return env.error.user_message;
    if (env.error?.message) return env.error.message;
  }
  return fallback;
}

// R3-02 — link-guard codes whose message names the offending SKUs
// ("<text>: SKU1, SKU2"). The BE also sends them as `error.skus`.
const SKU_ERROR_CODES = new Set([
  'COST_GUARD_VIOLATION',
  'ALREADY_LISTED',
  'BLUEPRINT_UNAVAILABLE',
  'PRICING_MISSING',
  'VARIANT_SPEC_OUT_OF_RANGE',
]);

/** The Printway SKUs an approve error names (SKU strings only — never a price). */
function extractErrorSkus(payload: unknown): string[] {
  if (!payload || typeof payload !== 'object') return [];
  const err = (payload as ApiErrorEnvelope).error;
  if (!err) return [];
  if (Array.isArray(err.skus)) {
    return err.skus.filter((x): x is string => typeof x === 'string' && x.length > 0);
  }
  if (err.code && SKU_ERROR_CODES.has(err.code) && typeof err.message === 'string') {
    const at = err.message.lastIndexOf(': ');
    if (at >= 0) {
      return err.message
        .slice(at + 2)
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
    }
  }
  return [];
}

/**
 * R3-02 — the localized reason plus the SKUs it concerns, so the admin knows
 * which SKUs to fix without opening the detail modal.
 */
function describeApproveError(
  payload: unknown,
  fallback: string,
  translate: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const reason = extractApiMessage(payload, fallback, translate);
  const skus = extractErrorSkus(payload);
  return skus.length > 0
    ? translate('product_approval.errors.withSkus', { reason, skus: skus.join(', ') })
    : reason;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function typePillClass(t: ApprovalType): string {
  if (t === 're_approval') return 'bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800';
  // admin self-create uses violet to visually distinguish from vendor flow.
  if (t === 'admin_self_create') return 'bg-violet-100 dark:bg-violet-950/40 text-violet-800 dark:text-violet-300 border border-violet-200 dark:border-violet-800';
  return 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800';
}

const DEFAULT_LIMIT = 20;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ProductApprovalPage(): JSX.Element {
  const { t } = useTranslation('catalog');
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Localized label for an approval type enum value.
  const typeLabel = useCallback(
    (type: ApprovalType): string => {
      if (type === 're_approval') return t('product_approval.types.reApproval');
      if (type === 'admin_self_create') return t('product_approval.types.adminSelfCreate');
      return t('product_approval.types.newSubmission');
    },
    [t],
  );

  // Generic API error fallback message (passed to extractApiMessage).
  const genericError = t('product_approval.errors.generic');

  const TYPE_OPTIONS = useMemo<Array<{ value: 'all' | ApprovalType; label: string }>>(
    () => [
      { value: 'all', label: t('product_approval.filters.allTypes') },
      { value: 'new_submission', label: t('product_approval.types.newSubmission') },
      { value: 're_approval', label: t('product_approval.types.reApproval') },
      // admin self-create unified queue option.
      { value: 'admin_self_create', label: t('product_approval.types.adminSelfCreate') },
    ],
    [t],
  );

  const STATUS_OPTIONS = useMemo<Array<{ value: 'all' | ApprovalStatus; label: string }>>(
    () => [
      { value: 'pending', label: t('product_approval.status.pending') },
      { value: 'approved', label: t('product_approval.status.approved') },
      { value: 'rejected', label: t('product_approval.status.rejected') },
      { value: 'all', label: t('product_approval.filters.allStatuses') },
    ],
    [t],
  );

  // Phase 8.2.2 — source chip options. All = both flows; Self-submit =
  // vendor-v3 UI flow (Phase 7); Excel = admin batch upload.
  const SOURCE_OPTIONS = useMemo<Array<{ value: 'all' | ApprovalSource; label: string }>>(
    () => [
      { value: 'all', label: t('product_approval.source.all') },
      { value: 'vendor_self_submit', label: t('product_approval.source.vendorSelfSubmit') },
      { value: 'admin_excel_upload', label: t('product_approval.source.adminExcelUpload') },
    ],
    [t],
  );

  // URL state
  const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const typeFilter = (searchParams.get('type') ?? 'all') as 'all' | ApprovalType;
  const statusFilter =
    (searchParams.get('status') ?? 'pending') as 'all' | ApprovalStatus;
  // Phase 8.2.2 — submission source filter chip persisted in URL.
  const sourceFilter = (searchParams.get('source') ?? 'all') as
    | 'all'
    | ApprovalSource;

  // Local state
  const [rows, setRows] = useState<ApprovalRow[]>([]);
  const [meta, setMeta] = useState<ApprovalListMeta>({
    page: 1,
    limit: DEFAULT_LIMIT,
    total: 0,
    pending_count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Action state
  const [actingId, setActingId] = useState<string | null>(null);

  // Phase 8.2.1 — current user context for 4-eyes invariant check.
  // Service-side enforces FOUR_EYES_REQUIRED + OVERRIDE_REASON_REQUIRED; the
  // FE just routes the modal so superadmin doesn't get a surprise 403.
  const currentUser = useAuthStore((s) => s.user);
  const currentUserId = currentUser?.id ?? null;
  const currentUserRoles = useMemo<string[]>(() => {
    if (!currentUser) return [];
    const out = new Set<string>();
    if (currentUser.primaryRole) out.add(currentUser.primaryRole);
    if (currentUser.activeRoleSlot) out.add(currentUser.activeRoleSlot);
    for (const r of currentUser.roles ?? []) out.add(r);
    return Array.from(out);
  }, [currentUser]);
  const isSuperadmin = currentUserRoles.includes('superadmin');

  // Diff modal state
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffRow, setDiffRow] = useState<ApprovalRow | null>(null);
  const [diffEntries, setDiffEntries] = useState<ApprovalDiffEntry[]>([]);
  const [diffMapping, setDiffMapping] = useState<ApprovalMapping | null>(null);
  // 2026-10-13 (D6) — approving a vendor submission puts its SKUs into the
  // routing pool; ask first, for single and bulk approve alike.
  const [confirmApprove, setConfirmApprove] = useState<
    { kind: 'one'; row: ApprovalRow } | { kind: 'bulk'; count: number } | null
  >(null);
  // R3-01 / R3-02 — number of enabled SKUs the confirmation is about (summed
  // over the selection for bulk). null = not known (unmapped / load failed).
  const [confirmSkus, setConfirmSkus] = useState<number | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  // R4-03 — bulk title counts the selection (plural via i18next `count`).
  const confirmTitle =
    confirmApprove?.kind === 'bulk'
      ? t('product_approval.confirmApprove.bulkTitle', { count: confirmApprove.count })
      : t('product_approval.confirmApprove.title');
  const confirmToken = useRef(0);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffDuplicates, setDiffDuplicates] = useState<
    Array<{ blueprintId: string; name: string; similarity: number }>
  >([]);

  // Reject modal state
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectRow, setRejectRow] = useState<ApprovalRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);

  // dbg-approval-bulk (2026-06-19) — multi-select + bulk approve/reject.
  // selectedIds holds approval row ids; bulk actions loop the existing single
  // approve/reject endpoints (no new BE). bulkRejectMode reuses the reject
  // dialog with one shared reason across the selection.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkRejectMode, setBulkRejectMode] = useState(false);

  // Phase 8.2.1 4-eyes override modal — opens when a superadmin
  // attempts to approve an admin-self-create row they themselves created.
  // overrideReason is REQUIRED by the BE service approveAdminSelfCreate
  // (OVERRIDE_REASON_REQUIRED 403 returned without it).
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideRow, setOverrideRow] = useState<ApprovalRow | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideError, setOverrideError] = useState<string | null>(null);

  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  // dbg (2026-07-06) UAT #437 — vendor picker source for the upload dialog.
  const [vendorOptions, setVendorOptions] = useState<
    Array<{ id: string; name: string }>
  >([]);
  // Lazy-load the vendor list the first time the upload dialog opens.
  useEffect(() => {
    if (!uploadOpen || vendorOptions.length > 0) return;
    let cancelled = false;
    void listVendorOptions()
      .then((opts) => {
        if (!cancelled) {
          setVendorOptions(opts.map((o) => ({ id: o.id, name: o.name })));
        }
      })
      .catch(() => {
        /* dialog falls back to its vendorLoadFail notice */
      });
    return () => {
      cancelled = true;
    };
  }, [uploadOpen, vendorOptions.length]);

  // -------------------------------------------------------------------------
  // Fetch list
  // -------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const client = apiClient() as unknown as LooseClient;
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(DEFAULT_LIMIT));
        if (typeFilter !== 'all') params.set('type', typeFilter);
        if (statusFilter !== 'all') params.set('status', statusFilter);
        if (sourceFilter !== 'all') params.set('source', sourceFilter);
        const { data, response, error } = await client.GET(
          `/api/v1/admin/product-approvals?${params.toString()}`,
        );
        if (cancelled) return;
        if (!response.ok) {
          toast({
            title: t('product_approval.toast.loadFailTitle'),
            description: extractApiMessage(error, genericError, t),
            variant: 'error',
          });
          setRows([]);
          return;
        }
        const body = data as
          | { data?: ApprovalRow[]; meta?: ApprovalListMeta }
          | ApprovalListResponse;
        const inner =
          body && typeof body === 'object' && 'data' in body && Array.isArray(body.data)
            ? (body as ApprovalListResponse)
            : (body as ApprovalListResponse);
        setRows(Array.isArray(inner.data) ? inner.data : []);
        if (inner.meta) setMeta(inner.meta);
      } catch (err) {
        if (cancelled) return;
        toast({
          title: t('product_approval.toast.loadFailTitle'),
          description: err instanceof Error ? err.message : t('product_approval.toast.unknownError'),
          variant: 'error',
        });
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, typeFilter, statusFilter, sourceFilter, refreshTick, toast, t, genericError]);

  // -------------------------------------------------------------------------
  // URL state helpers
  // -------------------------------------------------------------------------
  const updateParam = useCallback(
    (key: string, value: string | null): void => {
      const next = new URLSearchParams(searchParams);
      if (!value || value === 'all') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      // Reset page when filter changes.
      if (key !== 'page') next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // -------------------------------------------------------------------------
  // Approve / reject
  // -------------------------------------------------------------------------

  /**
   * Phase 8.2.1 — 4-eyes detection. Returns true when current user is
   * superadmin AND the approval row was created by the SAME user. In that
   * case approve() needs an overrideReason so the BE doesn't 403 with
   * OVERRIDE_REASON_REQUIRED.
   */
  function needsOverrideReason(row: ApprovalRow): boolean {
    if (row.actor_type !== 'admin') return false;
    if (!currentUserId || !row.actor_user_id) return false;
    if (row.actor_user_id !== currentUserId) return false;
    return isSuperadmin;
  }

  /** GET the approval detail and return its Printway mapping (null = none). */
  async function fetchMapping(id: string): Promise<ApprovalMapping | null> {
    const client = apiClient() as unknown as LooseClient;
    const { data, response, error } = await client.GET(
      `/api/v1/admin/product-approvals/${encodeURIComponent(id)}`,
    );
    if (!response.ok) throw new Error(extractApiMessage(error, genericError, t));
    const body = data as { data?: { mapping?: ApprovalMapping | null } } | { mapping?: ApprovalMapping | null };
    const inner =
      body && typeof body === 'object' && 'data' in body && body.data
        ? body.data
        : (body as { mapping?: ApprovalMapping | null });
    return inner?.mapping ?? null;
  }

  const enabledSkus = (m: ApprovalMapping | null): number =>
    m ? m.skus.filter((s) => s.enabled).length : 0;

  /**
   * R3-01 / R3-02 — open the D6 confirmation and resolve how many SKUs it
   * puts into the routing pool. The dialog shows a spinner and keeps Approve
   * disabled until the count is known; a failed load is toasted and the
   * dialog falls back to the generic wording.
   */
  function openConfirm(target: { kind: 'one'; row: ApprovalRow } | { kind: 'bulk'; rows: ApprovalRow[] }): void {
    const token = ++confirmToken.current;
    setConfirmSkus(null);
    setConfirmLoading(false);
    if (target.kind === 'one') {
      setConfirmApprove({ kind: 'one', row: target.row });
      if (!target.row.mapped) return;
      if (diffRow?.id === target.row.id && diffMapping) {
        setConfirmSkus(enabledSkus(diffMapping));
        return;
      }
    } else {
      setConfirmApprove({ kind: 'bulk', count: target.rows.length });
    }
    const mappedRows = target.kind === 'one' ? [target.row] : target.rows.filter((r) => r.mapped);
    if (mappedRows.length === 0) return;
    setConfirmLoading(true);
    void Promise.all(mappedRows.map((r) => fetchMapping(r.id)))
      .then((maps) => {
        if (confirmToken.current !== token) return;
        setConfirmSkus(maps.reduce((sum, m) => sum + enabledSkus(m), 0));
      })
      .catch((err: unknown) => {
        if (confirmToken.current !== token) return;
        toast({
          title: t('product_approval.confirmApprove.loadFailed'),
          description: err instanceof Error ? err.message : genericError,
          variant: 'error',
        });
      })
      .finally(() => {
        if (confirmToken.current === token) setConfirmLoading(false);
      });
  }

  function openApproveFlow(row: ApprovalRow): void {
    if (row.actor_type !== 'admin') {
      openConfirm({ kind: 'one', row });
      return;
    }
    if (needsOverrideReason(row)) {
      setOverrideRow(row);
      setOverrideReason('');
      setOverrideError(null);
      setOverrideOpen(true);
      return;
    }
    void handleApprove(row);
  }

  async function handleApprove(
    row: ApprovalRow,
    extraOverrideReason?: string,
  ): Promise<void> {
    setActingId(row.id);
    try {
      const body: Record<string, unknown> = {};
      const trimmed = (extraOverrideReason ?? '').trim();
      if (trimmed.length > 0) body['overrideReason'] = trimmed;

      const client = apiClient() as unknown as LooseClient;
      const { response, error } = await client.POST(
        `/api/v1/admin/product-approvals/${encodeURIComponent(row.id)}/approve`,
        {
          body,
          headers: { 'Content-Type': 'application/json' },
        },
      );
      if (!response.ok) {
        toast({
          title: t('product_approval.toast.approveFailed'),
          description: t('product_approval.errors.forProduct', {
            product: row.product_name,
            reason: describeApproveError(error, genericError, t),
          }),
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('product_approval.toast.approved'),
        description: row.product_name,
        variant: 'success',
      });
      setRefreshTick((tick) => tick + 1);
      if (diffRow?.id === row.id) {
        setDiffOpen(false);
        setDiffRow(null);
      }
      // Close override modal on success.
      if (overrideRow?.id === row.id) {
        setOverrideOpen(false);
        setOverrideRow(null);
      }
    } finally {
      setActingId(null);
    }
  }

  async function submitOverride(): Promise<void> {
    if (!overrideRow) return;
    const reason = overrideReason.trim();
    if (reason.length < 1 || reason.length > 2000) {
      setOverrideError(t('product_approval.override.errorLength'));
      return;
    }
    setOverrideError(null);
    await handleApprove(overrideRow, reason);
  }

  function openRejectDialog(row: ApprovalRow): void {
    setBulkRejectMode(false);
    setRejectRow(row);
    setRejectReason('');
    setRejectError(null);
    setRejectOpen(true);
  }

  // dbg-approval-bulk — selection helpers. Only PENDING rows are selectable
  // (approve/reject act on pending). selectedPending is the live intersection
  // (robust to a page refresh changing the visible rows).
  const pendingRows = rows.filter((r) => r.status === 'pending');
  const selectedPending = pendingRows.filter((r) => selectedIds.has(r.id));
  const allPendingSelected =
    pendingRows.length > 0 && pendingRows.every((r) => selectedIds.has(r.id));

  function toggleSelect(id: string): void {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleSelectAll(): void {
    setSelectedIds(allPendingSelected ? new Set() : new Set(pendingRows.map((r) => r.id)));
  }

  async function bulkApprove(): Promise<void> {
    if (selectedPending.length === 0) return;
    setBulkBusy(true);
    try {
      const client = apiClient() as unknown as LooseClient;
      const results = await Promise.allSettled(
        selectedPending.map((row) =>
          client
            .POST(
              `/api/v1/admin/product-approvals/${encodeURIComponent(row.id)}/approve`,
              { body: {}, headers: { 'Content-Type': 'application/json' } },
            )
            .then((r) => {
              // R3-02 — name the product and its SKUs: "<product>: <reason> — SKU1, SKU2".
              if (!r.response.ok) {
                throw new Error(
                  t('product_approval.errors.forProduct', {
                    product: row.product_name,
                    reason: describeApproveError(r.error, genericError, t),
                  }),
                );
              }
            }),
        ),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      // dbg-20260910c — report the server's OWN reason, not a guess.
      //
      // The description used to be a fixed hint printed whenever anything
      // failed: "may need an override reason (approve rows individually)".
      // For the reported case that advice was wrong — an admin approving a
      // blueprint they created themselves gets FOUR_EYES_REQUIRED, where no
      // override reason exists and a different admin has to approve. The
      // message was already built two lines up (`extractApiMessage` localizes
      // the BE error code) and then thrown away with the Error it was wrapped
      // in. Distinct reasons are de-duplicated so N rows failing for one cause
      // read as one line.
      const reasons = [
        ...new Set(
          results.flatMap((r) =>
            r.status === 'rejected'
              ? [r.reason instanceof Error ? r.reason.message : String(r.reason)]
              : [],
          ),
        ),
      ];
      toast({
        title: t('product_approval.toast.approvedCount', { ok, total: results.length }),
        description: reasons.length > 0 ? reasons.join('\n') : undefined,
        variant: reasons.length > 0 ? 'error' : 'success',
      });
      setSelectedIds(new Set());
      setRefreshTick((tick) => tick + 1);
    } finally {
      setBulkBusy(false);
    }
  }

  function openBulkReject(): void {
    if (selectedPending.length === 0) return;
    setBulkRejectMode(true);
    setRejectRow(null);
    setRejectReason('');
    setRejectError(null);
    setRejectOpen(true);
  }

  async function submitReject(): Promise<void> {
    const reason = rejectReason.trim();
    if (reason.length < 10 || reason.length > 500) {
      setRejectError(t('product_approval.reject.errorLength'));
      return;
    }
    // dbg-approval-bulk — bulk path: loop the single reject endpoint with one
    // shared reason across the selection.
    if (bulkRejectMode) {
      setRejectSubmitting(true);
      setRejectError(null);
      try {
        const client = apiClient() as unknown as LooseClient;
        const results = await Promise.allSettled(
          selectedPending.map((row) =>
            client
              .POST(
                `/api/v1/admin/product-approvals/${encodeURIComponent(row.id)}/reject`,
                { body: { reason }, headers: { 'Content-Type': 'application/json' } },
              )
              .then((r) => {
                if (!r.response.ok) throw new Error(extractApiMessage(r.error, genericError, t));
              }),
          ),
        );
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        const fail = results.length - ok;
        toast({
          title: t('product_approval.toast.rejectedCount', { ok, total: results.length }),
          description: fail > 0 ? t('product_approval.toast.failCount', { fail }) : undefined,
          variant: fail > 0 ? 'error' : 'success',
        });
        setRejectOpen(false);
        setBulkRejectMode(false);
        setRejectReason('');
        setSelectedIds(new Set());
        setRefreshTick((tick) => tick + 1);
      } finally {
        setRejectSubmitting(false);
      }
      return;
    }
    if (!rejectRow) return;
    setRejectSubmitting(true);
    setRejectError(null);
    try {
      const client = apiClient() as unknown as LooseClient;
      const { response, error } = await client.POST(
        `/api/v1/admin/product-approvals/${encodeURIComponent(rejectRow.id)}/reject`,
        {
          body: { reason },
          headers: { 'Content-Type': 'application/json' },
        },
      );
      if (!response.ok) {
        setRejectError(extractApiMessage(error, genericError, t));
        return;
      }
      toast({
        title: t('product_approval.toast.rejected'),
        description: rejectRow.product_name,
        variant: 'success',
      });
      setRejectOpen(false);
      setRejectRow(null);
      setRejectReason('');
      setRefreshTick((tick) => tick + 1);
    } finally {
      setRejectSubmitting(false);
    }
  }

  // -------------------------------------------------------------------------
  // Diff modal
  // -------------------------------------------------------------------------
  async function openDiff(row: ApprovalRow): Promise<void> {
    setDiffRow(row);
    setDiffEntries([]);
    setDiffMapping(null);
    setDiffDuplicates([]);
    setDiffOpen(true);
    setDiffLoading(true);
    try {
      // Diff payload comes from a sibling GET (BE service exposes it via list
      // detail). For minimal v1 surface we re-fetch the full approval list
      // entry with original payload via the dedicated detail endpoint when
      // available, otherwise fall back to empty diff. The 5-field structure
      // is shared with the BE diffTriggerFields helper.
      const client = apiClient() as unknown as LooseClient;
      const { data, response } = await client.GET(
        `/api/v1/admin/product-approvals/${encodeURIComponent(row.id)}`,
      );
      if (response.ok) {
        const body = data as
          | {
              data?: {
                diff?: ApprovalDiffEntry[];
                duplicate_warnings?: Array<{
                  blueprintId: string;
                  name: string;
                  similarity: number;
                }>;
              };
            }
          | {
              diff?: ApprovalDiffEntry[];
              duplicate_warnings?: Array<{
                blueprintId: string;
                name: string;
                similarity: number;
              }>;
            };
        const inner =
          body && typeof body === 'object' && 'data' in body && body.data
            ? body.data
            : (body as { diff?: ApprovalDiffEntry[] });
        if (Array.isArray(inner?.diff)) setDiffEntries(inner.diff);
        const mapping = (inner as { mapping?: ApprovalMapping | null })?.mapping;
        setDiffMapping(mapping ?? null);
        const dup = (inner as { duplicate_warnings?: typeof diffDuplicates })
          ?.duplicate_warnings;
        if (Array.isArray(dup)) setDiffDuplicates(dup);
      }
      // If duplicate_warnings already present on the list row, surface them too.
      if (row.duplicate_warnings && row.duplicate_warnings.length > 0) {
        setDiffDuplicates(row.duplicate_warnings);
      }
    } finally {
      setDiffLoading(false);
    }
  }

  // -------------------------------------------------------------------------
  // Pagination
  // -------------------------------------------------------------------------
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(meta.total / Math.max(1, meta.limit))),
    [meta.total, meta.limit],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title={t('product_approval.title')}
        subtitle={t('product_approval.pageSubtitle', {
          fields: 'processingTime, baseCost, capacityPerDay, shippingZones, productionSpecs',
        })}
        actions={
          <Button
            onClick={() => setUploadOpen(true)}
            data-testid="product-approval-upload-btn"
          >
            {t('product_approval.actions.uploadExcel')}
          </Button>
        }
      />

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="min-w-[180px]">
              <label className="block text-xs font-medium text-pw-text-muted mb-1">
                {t('product_approval.filters.typeLabel')}
              </label>
              <Select
                value={typeFilter}
                onValueChange={(value) => updateParam('type', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <label className="block text-xs font-medium text-pw-text-muted mb-1">
                {t('product_approval.filters.statusLabel')}
              </label>
              <Select
                value={statusFilter}
                onValueChange={(value) => updateParam('status', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[200px]" data-testid="approval-source-filter">
              <label className="block text-xs font-medium text-pw-text-muted mb-1">
                {t('product_approval.filters.sourceLabel')}
              </label>
              <Select
                value={sourceFilter}
                onValueChange={(value) => updateParam('source', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto text-sm text-pw-text-muted">
              <span className="font-medium text-amber-700 dark:text-amber-300">
                {meta.pending_count}
              </span>{' '}
              {t('product_approval.summary.pendingLabel')} · {meta.total}{' '}
              {t('product_approval.summary.totalLabel')}
            </div>
          </div>

          {/* dbg-approval-bulk — bulk action bar (shows when ≥1 pending row selected) */}
          {selectedPending.length > 0 ? (
            <div
              className="mb-2 flex items-center gap-3 rounded-md border border-pw-primary/40 bg-pw-primary-soft/40 px-3 py-2 text-sm"
              data-testid="product-approval-bulk-bar"
            >
              <span className="font-medium text-pw-text">
                {t('product_approval.bulk.selected', { count: selectedPending.length })}
              </span>
              <Button
                size="sm"
                onClick={() => openConfirm({ kind: 'bulk', rows: selectedPending })}
                disabled={bulkBusy}
                data-testid="product-approval-bulk-approve-btn"
              >
                {bulkBusy
                  ? t('product_approval.bulk.approving')
                  : t('product_approval.bulk.approve', { count: selectedPending.length })}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={openBulkReject}
                disabled={bulkBusy}
                data-testid="product-approval-bulk-reject-btn"
              >
                {t('product_approval.bulk.reject', { count: selectedPending.length })}
              </Button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="ml-auto text-xs text-pw-text-muted hover:text-pw-text"
              >
                {t('product_approval.bulk.clear')}
              </button>
            </div>
          ) : null}

          <div className="overflow-x-auto border border-pw-border rounded">
            <table className="w-full text-sm">
              <thead className="bg-pw-surface border-b border-pw-border">
                <tr>
                  <th className="w-8 py-2 px-3">
                    <input
                      type="checkbox"
                      checked={allPendingSelected}
                      onChange={toggleSelectAll}
                      disabled={pendingRows.length === 0}
                      aria-label={t('product_approval.table.selectAllAria')}
                      data-testid="product-approval-select-all"
                    />
                  </th>
                  <th className="text-left py-2 px-3 font-medium text-pw-text-muted">
                    {t('product_approval.columns.vendor')}
                  </th>
                  <th className="text-left py-2 px-3 font-medium text-pw-text-muted">
                    {t('product_approval.columns.product')}
                  </th>
                  <th className="text-left py-2 px-3 font-medium text-pw-text-muted">
                    {t('product_approval.columns.category')}
                  </th>
                  <th className="text-left py-2 px-3 font-medium text-pw-text-muted">
                    {t('product_approval.columns.submittedAt')}
                  </th>
                  <th className="text-left py-2 px-3 font-medium text-pw-text-muted">
                    {t('product_approval.columns.type')}
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-pw-text-muted">
                    {t('product_approval.columns.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-pw-text-muted">
                      {t('product_approval.table.loading')}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-pw-text-muted">
                      {t('product_approval.table.empty')}
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className="border-b border-pw-border">
                      <td className="w-8 py-2 px-3 align-top">
                        {row.status === 'pending' ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(row.id)}
                            onChange={() => toggleSelect(row.id)}
                            aria-label={t('product_approval.table.selectRowAria', { name: row.product_name })}
                            data-testid={`product-approval-select-${row.id}`}
                          />
                        ) : null}
                      </td>
                      <td className="py-2 px-3">
                        {row.actor_type === 'admin' ? (
                          <div className="flex flex-col gap-0.5">
                            <span
                              className="inline-flex w-fit items-center rounded-md border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/40 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:text-violet-300"
                              data-testid={`product-approval-actor-badge-${row.id}`}
                            >
                              {t('product_approval.types.adminSelfCreate')}
                            </span>
                            <span className="text-xs text-pw-text-muted">
                              {row.actor_email || '—'}
                            </span>
                          </div>
                        ) : row.vendor_id ? (
                          // UAT #284: link the vendor name to the vendor detail
                          // page (mirrors the product cell's <a> pattern below).
                          <a
                            href={`/vendors/${row.vendor_id}`}
                            className="text-blue-600 dark:text-blue-300 hover:underline"
                            data-testid={`product-approval-vendor-link-${row.id}`}
                          >
                            {row.vendor_name || '—'}
                          </a>
                        ) : (
                          row.vendor_name || '—'
                        )}
                      </td>
                      <td className="py-2 px-3 font-medium text-pw-text">
                        {/* dbg-approval-name-link — product name links to the
                            blueprint detail so reviewers can inspect the
                            blueprint steps before approving. admin self-create
                            rows carry the id on approved_blueprint_id; existing
                            blueprints (re-approval) on original_blueprint_id. */}
                        {(() => {
                          const blueprintId =
                            row.original_blueprint_id ??
                            row.approved_blueprint_id ??
                            null;
                          return blueprintId ? (
                            <a
                              href={`/products/blueprints/${blueprintId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-300 hover:underline"
                              title={t('product_approval.links.openBlueprint')}
                              data-testid={`product-approval-product-name-link-${row.id}`}
                            >
                              {row.product_name}
                            </a>
                          ) : (
                            <div>{row.product_name}</div>
                          );
                        })()}
                        {row.vendor_product_id && row.mapped === false ? (
                          // D9 — a vendor product with no Printway mapping
                          // (legacy, source_id = null): approving it mints a
                          // Draft blueprint instead of linking SKUs.
                          <span
                            className="mt-1 inline-flex rounded-full border border-pw-border bg-pw-surface px-2 py-0.5 text-xs text-pw-text-muted"
                            data-testid={`product-approval-unmapped-badge-${row.id}`}
                          >
                            {t('product_approval.unmappedBadge')}
                          </span>
                        ) : null}
                        {row.vendor_product_id && row.vendor_id ? (
                          <div className="mt-1 text-xs">
                            <a
                              href={`/vendors/${row.vendor_id}/products/${row.vendor_product_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-300 hover:underline"
                              data-testid={`product-approval-vendor-product-link-${row.id}`}
                            >
                              {t('product_approval.links.viewVendorProduct')} ↗
                            </a>
                          </div>
                        ) : null}
                        {row.apply_to_vendor_ids && row.apply_to_vendor_ids.length > 0 ? (
                          <div className="mt-1 text-xs text-pw-text-muted">
                            {t('product_approval.applyTargets', { count: row.apply_to_vendor_ids.length })}
                          </div>
                        ) : null}
                      </td>
                      <td className="py-2 px-3 text-pw-text">
                        {row.category_name ?? '—'}
                      </td>
                      <td className="py-2 px-3 text-pw-text-muted">
                        {formatDate(row.submitted_at)}
                      </td>
                      <td className="py-2 px-3">
                        <div className="space-y-1">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded text-xs ${typePillClass(row.type)}`}
                          >
                            {typeLabel(row.type)}
                          </span>
                          {row.source ? (
                            <div>
                              <span
                                className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${
                                  row.source === 'vendor_self_submit'
                                    ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                                    : 'bg-pw-surface text-pw-text border-pw-border'
                                }`}
                              >
                                {row.source === 'vendor_self_submit'
                                  ? t('product_approval.sourcePill.vendorSubmit')
                                  : t('product_approval.sourcePill.adminUpload')}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="inline-flex gap-2">
                          {(row.type === 're_approval' && row.has_diff) || row.mapped ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void openDiff(row)}
                              data-testid={`product-approval-diff-btn-${row.id}`}
                            >
                              {row.has_diff ? t('product_approval.actions.diff') : t('product_approval.actions.details')}
                            </Button>
                          ) : null}
                          {row.status === 'pending' ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => openApproveFlow(row)}
                                disabled={actingId === row.id}
                                data-testid={`product-approval-approve-btn-${row.id}`}
                              >
                                {t('product_approval.actions.approve')}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => openRejectDialog(row)}
                                disabled={actingId === row.id}
                                data-testid={`product-approval-reject-btn-${row.id}`}
                              >
                                {t('product_approval.actions.reject')}
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-pw-text-muted">
                              {t(`product_approval.status.${row.status}`)}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={page}
            pageCount={totalPages}
            totalRows={meta.total}
            onChange={(next) => updateParam('page', String(next))}
            testId="product-approval-pagination"
          />
        </CardContent>
      </Card>

      {/* 2026-10-13 (D6) — approve confirmation with the routing consequence. */}
      <Dialog
        open={confirmApprove !== null}
        onOpenChange={(next) => {
          if (!next) {
            confirmToken.current += 1;
            setConfirmApprove(null);
          }
        }}
        aria-label={confirmTitle}
        className="max-w-lg"
      >
        <div data-testid="product-approval-confirm">
          <DialogHeader>
            <DialogTitle>{confirmTitle}</DialogTitle>
            <p className="text-sm text-pw-text-muted">
              {confirmLoading ? (
                <span className="inline-flex items-center gap-2" data-testid="product-approval-confirm-loading">
                  <Spinner size="sm" />
                  {t('product_approval.confirmApprove.loading')}
                </span>
              ) : confirmApprove?.kind === 'bulk' ? (
                confirmSkus !== null && confirmSkus > 0
                  ? t('product_approval.confirmApprove.bulkBodySkus', {
                      count: confirmApprove.count,
                      skus: confirmSkus,
                    })
                  : t('product_approval.confirmApprove.bulkBody', { count: confirmApprove.count })
              ) : confirmApprove?.kind === 'one' && confirmApprove.row.mapped && confirmSkus !== null ? (
                t('product_approval.confirmApprove.body', { count: confirmSkus })
              ) : (
                t('product_approval.confirmApprove.bodyGeneric')
              )}
            </p>
            <p className="text-sm text-pw-text-muted">{t('product_approval.confirmApprove.routing')}</p>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                confirmToken.current += 1;
                setConfirmApprove(null);
              }}
              data-testid="product-approval-confirm-cancel"
            >
              {t('product_approval.diffModal.actions.close')}
            </Button>
            <Button
              data-testid="product-approval-confirm-ok"
              disabled={confirmLoading}
              onClick={() => {
                const c = confirmApprove;
                confirmToken.current += 1;
                setConfirmApprove(null);
                if (!c) return;
                if (c.kind === 'bulk') void bulkApprove();
                else if (needsOverrideReason(c.row)) {
                  setOverrideRow(c.row);
                  setOverrideReason('');
                  setOverrideError(null);
                  setOverrideOpen(true);
                } else void handleApprove(c.row);
              }}
            >
              {t('product_approval.actions.approve')}
            </Button>
          </DialogFooter>
        </div>
      </Dialog>

      {/* Diff modal */}
      {diffRow ? (
        <ApprovalDiffModal
          open={diffOpen}
          onClose={() => {
            setDiffOpen(false);
            setDiffRow(null);
          }}
          vendorName={diffRow.vendor_name}
          productName={diffRow.product_name}
          entries={diffEntries}
          mapping={diffMapping}
          onApprove={
            diffRow.status === 'pending' ? () => openApproveFlow(diffRow) : undefined
          }
          onReject={
            diffRow.status === 'pending' ? () => openRejectDialog(diffRow) : undefined
          }
          approveLoading={actingId === diffRow.id}
        />
      ) : null}

      {/* Duplicate detection banner — surfaced inside the diff modal
          context but rendered at page level as a sticky alert when the
          loaded diff row has duplicates. Inline rather than separate modal
          per UI-SPEC §AC-10. */}
      {diffOpen && diffDuplicates.length > 0 ? (
        <div
          className="fixed top-4 right-4 max-w-md border border-amber-300 bg-amber-50 dark:bg-amber-950/40 rounded p-3 text-sm shadow-lg z-50"
          data-testid="product-approval-duplicate-banner"
        >
          <div className="font-medium text-amber-900 mb-1">
            {t('product_approval.duplicate.title', { count: diffDuplicates.length })}
          </div>
          <ul className="list-disc pl-5 space-y-0.5 text-amber-800 dark:text-amber-300">
            {diffDuplicates.map((d) => (
              <li key={d.blueprintId}>
                {d.name}
                <span className="text-xs text-amber-700 dark:text-amber-300 ml-1">
                  {t('product_approval.duplicate.similarity', { pct: Math.round(d.similarity * 100) })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Reject dialog — single (rejectRow) or bulk (bulkRejectMode) */}
      {rejectRow || bulkRejectMode ? (
        <Dialog
          open={rejectOpen}
          onOpenChange={(next) => {
            if (!next) {
              setRejectOpen(false);
              setRejectRow(null);
              setBulkRejectMode(false);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {bulkRejectMode
                ? t('product_approval.reject.bulkTitle', { count: selectedPending.length })
                : t('product_approval.reject.title', { name: rejectRow?.product_name ?? '' })}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-4 space-y-2">
            <p className="text-sm text-pw-text-muted">
              {t('product_approval.reject.hint')}
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full border border-pw-border rounded p-2 min-h-[120px] text-sm"
              placeholder={t('product_approval.reject.placeholder')}
              maxLength={500}
            />
            <div className="flex justify-between text-xs text-pw-text-muted">
              <span>
                {t('product_approval.reject.counter', { count: rejectReason.trim().length })}
              </span>
              {rejectError ? (
                <span className="text-rose-600 dark:text-rose-300">{rejectError}</span>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setRejectOpen(false);
                setRejectRow(null);
                setBulkRejectMode(false);
              }}
            >
              {t('product_approval.actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void submitReject()}
              disabled={rejectSubmitting}
            >
              {rejectSubmitting
                ? t('product_approval.reject.submitting')
                : t('product_approval.reject.confirm')}
            </Button>
          </DialogFooter>
        </Dialog>
      ) : null}

      {/* Phase 8.2.1 — 4-eyes override modal for self-approve */}
      {overrideRow ? (
        <Dialog
          open={overrideOpen}
          onOpenChange={(next) => {
            if (!next && actingId !== overrideRow.id) {
              setOverrideOpen(false);
              setOverrideRow(null);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {t('product_approval.override.title', { name: overrideRow.product_name })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 px-1">
            <p className="text-sm text-pw-text">
              {t('product_approval.override.bodyBefore')}
              <strong>superadmin</strong>
              {t('product_approval.override.bodyAfter')}
            </p>
            <label className="block text-sm font-medium text-pw-text">
              {t('product_approval.override.reasonLabel')} <span className="text-rose-600 dark:text-rose-300">*</span>
            </label>
            <Input
              type="text"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder={t('product_approval.override.placeholder')}
              maxLength={2000}
              data-testid="product-approval-override-reason"
            />
            {overrideError ? (
              <p className="text-xs text-rose-600 dark:text-rose-300">{overrideError}</p>
            ) : (
              <p className="text-xs text-pw-text-muted">
                {t('product_approval.override.hint')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOverrideOpen(false);
                setOverrideRow(null);
              }}
              disabled={actingId === overrideRow.id}
            >
              {t('product_approval.actions.cancel')}
            </Button>
            <Button
              onClick={() => void submitOverride()}
              disabled={actingId === overrideRow.id}
              data-testid="product-approval-override-submit"
            >
              {actingId === overrideRow.id
                ? t('product_approval.override.submitting')
                : t('product_approval.override.confirm')}
            </Button>
          </DialogFooter>
        </Dialog>
      ) : null}

      {/* Upload dialog */}
      <ApprovalUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => setRefreshTick((tick) => tick + 1)}
        vendorOptions={vendorOptions}
      />

      {/* Suppress unused-state setter warning when diff is loading silently */}
      {diffLoading ? <span className="sr-only">{t('product_approval.diff.loading')}</span> : null}
    </div>
  );
}

export default ProductApprovalPage;
